/* global Element, IntersectionObserverEntry */
import {
  useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type RefObject, type SetStateAction,
} from 'react';

/** A countdown that begins only once its subject has been seen, and fires once. */
export interface SeenCountdownState {
  /** Seconds left; null until the subject has been seen. */
  secondsLeft: number | null;
  /** Wait, a focus on a field or an edit retired the countdown. */
  stopped: boolean;
  /** The countdown ran out; `consume()` hands the one automatic action to its owner. */
  due: boolean;
}

interface CountdownState extends SeenCountdownState {
  key: string | null;
  seen: boolean;
}

type SetCountdown = Dispatch<SetStateAction<CountdownState>>;

const idle = (key: string | null): CountdownState => (
  { key, seen: false, secondsLeft: null, stopped: false, due: false }
);

const tick = (prev: CountdownState): CountdownState => {
  if (prev.stopped || prev.due || prev.secondsLeft === null) return prev;
  const secondsLeft = prev.secondsLeft - 1;

  return secondsLeft <= 0 ? { ...prev, secondsLeft: 0, due: true } : { ...prev, secondsLeft };
};

/** One tick per second while running and the page is in front. */
const useTicker = (running: boolean, setState: SetCountdown): void => {
  useEffect(() => {
    if (!running) return undefined;
    const timer = globalThis.setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') setState(tick);
    }, 1000);

    return () => globalThis.clearInterval(timer);
  }, [running, setState]);
};

/** The stable controls over a countdown: see it, stand it down, claim its one firing. */
const useCountdownControls = (state: CountdownState, seconds: number, setState: SetCountdown) => {
  const consumedKey = useRef<string | null>(null);
  const markSeen = useCallback(() => {
    setState((prev) => (prev.seen ? prev : { ...prev, seen: true, secondsLeft: seconds }));
  }, [seconds, setState]);
  const stop = useCallback(() => {
    setState((prev) => (prev.stopped ? prev : { ...prev, stopped: true }));
  }, [setState]);
  const consume = useCallback(() => {
    if (!state.due || state.stopped || !state.key || consumedKey.current === state.key) return false;
    consumedKey.current = state.key;

    return true;
  }, [state.due, state.key, state.stopped]);

  return { markSeen, stop, consume };
};

/**
 * Count `seconds` down once per second after `markSeen()`, while the page is in
 * front and `paused` is false, then mark the action due. A new `key` starts
 * over; `null` idles. `consume()` returns true exactly once per key once due.
 */
export const useSeenCountdown = (key: string | null, seconds: number, paused: boolean) => {
  const [state, setState] = useState<CountdownState>(() => idle(key));

  // A new subject starts from scratch (state adjusted during render, keyed by it).
  if (state.key !== key) setState(idle(key));
  useTicker(Boolean(key) && state.seen && !paused && !state.stopped && !state.due, setState);
  const controls = useCountdownControls(state, seconds, setState);
  const countdown = useMemo<SeenCountdownState>(
    () => ({ secondsLeft: state.secondsLeft, stopped: state.stopped, due: state.due }),
    [state.due, state.secondsLeft, state.stopped],
  );

  return { state: countdown, ...controls };
};

const SEEN_RATIO = 0.5;
/** A tall subject counts as seen once this much of it is in view, even below the ratio. */
const SEEN_HEIGHT_PX = 160;

const inView = (entries: IntersectionObserverEntry[]): boolean => entries.some((entry) => entry.isIntersecting
  && (entry.intersectionRatio >= SEEN_RATIO || entry.intersectionRect.height >= SEEN_HEIGHT_PX));

/** Watch `subject` until it is mostly on screen in a foreground tab; returns the teardown. */
const watchSeen = (subject: Element, markSeen: () => void): (() => void) => {
  let seen = false;
  const observer = new globalThis.IntersectionObserver((entries) => {
    if (seen || document.visibilityState !== 'visible' || !inView(entries)) return;
    seen = true;
    markSeen();
    observer.disconnect();
  }, { threshold: [0, SEEN_RATIO] });
  // A subject already on screen while the tab sat in the background never
  // crosses the threshold again: re-arm when the tab comes forward.
  const recheck = () => {
    if (seen || document.visibilityState !== 'visible') return;
    observer.unobserve(subject);
    observer.observe(subject);
  };

  observer.observe(subject);
  document.addEventListener('visibilitychange', recheck);

  return () => {
    observer.disconnect();
    document.removeEventListener('visibilitychange', recheck);
  };
};

/**
 * Call `markSeen` once the element (or the subject `resolve` picks from it, such
 * as the message it sits in) is mostly on screen in a foreground tab.
 */
export const useSeenElement = <T extends Element>(
  ref: RefObject<T | null>,
  enabled: boolean,
  markSeen: () => void,
  resolve: (element: T) => Element = (element) => element,
): void => {
  useEffect(() => {
    const element = ref.current;

    if (!enabled || !element || typeof globalThis.IntersectionObserver !== 'function') return undefined;

    return watchSeen(resolve(element), markSeen);
  }, [enabled, markSeen, ref, resolve]);
};
